import type { ShareLinkView } from "./api-client.ts";

export interface ShareCopyResult {
  copied: boolean;
  link: ShareLinkView;
  payload: string;
}

interface ClipboardPort<ClipboardEntry> {
  write?(items: ClipboardEntry[]): Promise<void>;
  writeText?(payload: string): Promise<void>;
}

export function sharePreparationErrorMessage(error: unknown): string {
  void error;
  return "Could not prepare sharing";
}

export async function tryCopyRecipientLink<ClipboardEntry = unknown>(options: {
  link: ShareLinkView | Promise<ShareLinkView>;
  absoluteUrl(path: string): string;
  onCopyFailed(link: ShareLinkView, payload: string): void;
  clipboard?: ClipboardPort<ClipboardEntry>;
  createClipboardItem?(representations: Record<"text/plain", Promise<Blob>>): ClipboardEntry;
}): Promise<ShareCopyResult> {
  const linkPromise = Promise.resolve(options.link);
  const payloadPromise = linkPromise.then((link) => options.absoluteUrl(link.pageUrl));
  const copiedPromise = (() => {
    try {
      const write = options.clipboard?.write;
      if (typeof write === "function" && options.createClipboardItem) {
        const item = options.createClipboardItem({
          "text/plain": payloadPromise.then(
            (payload) => new Blob([payload], { type: "text/plain" }),
          ),
        });
        return write.call(options.clipboard, [item]).then(
          () => true,
          () => false,
        );
      }

      const writeText = options.clipboard?.writeText;
      if (typeof writeText !== "function") {
        return Promise.reject(new Error("Clipboard API unavailable")).then(
          () => true,
          () => false,
        );
      }
      return payloadPromise
        .then((payload) => writeText.call(options.clipboard, payload))
        .then(
          () => true,
          () => false,
        );
    } catch {
      return Promise.resolve(false);
    }
  })();

  const link = await linkPromise;
  const payload = await payloadPromise;
  const copied = await copiedPromise;
  if (copied) return { copied: true, link, payload };

  options.onCopyFailed(link, payload);
  return { copied: false, link, payload };
}
